import { apiClient } from '../../api/client';

/**
 * Экраны Э-Н1 «Сеть и филиалы» и Э-Н2 «Филиал» (`s104`).
 *
 * ⛔ ЗДЕСЬ НЕТ НИ ОДНОГО ВЫЧИСЛЕНИЯ, И ЭТО НЕ АККУРАТНОСТЬ, А ЗАКОН ЭКРАНА
 * (07-ekrany §9 п.8): «ни одного числа мимо расчётного слоя». Сервер отдаёт и
 * значение, и уже готовую подпись к нему (`display`) — фронт печатает строку.
 * Сложи он хоть раз два числа сам — и у величины появился бы второй счёт,
 * который однажды разойдётся с отчётом, а заметит это владелец, а не тест.
 * Сторож — `tests/network-no-arithmetic.test.ts`.
 */

export interface NetworkDefault {
  key: string;
  label: string;
  value: unknown;
  display: string;
  caption: string;
}

export interface FilialRow {
  id: number;
  name: string;
  legal_entity: string | null;
  hours_display: string;
  hours_override: boolean;
  deposit_display: string;
  deposit_origin: 'own' | 'network';
  blocks_display: string;
  cash_registers: number;
}

/** Настройка уровня сети с полем правки (`s105`, правка 1). */
/** Смысловой блок настроек (`s105`, правка 7): порядок и подпись задаёт сервер. */
export interface SettingGroup {
  key: string;
  label: string;
  caption: string;
}

export interface NetworkSetting {
  key: string;
  label: string;
  unit: string;
  /** Ключ блока из `setting_groups` — раскладку решает расчётный слой. */
  group: string;
  value: unknown;
  display: string;
  edit_value: unknown;
  edit_suffix: string | null;
  editable: boolean;
  origin: 'network' | 'code_default';
  origin_label: string;
  not_editable_reason: string | null;
  level: string;
  options: Array<{ value: string; label: string }> | null;
}

/** Одна правка настройки в следе — раскрываемой строкой (`s105`, правка 1). */
export interface SettingHistoryEntry {
  action: string;
  action_label: string;
  author: string;
  at: string;
  from: string;
  to: string;
}

/** Часы приёма филиала: вывеска, а не служебное окно (`s105`, правка 4). */
export interface ReceptionHours {
  /** `null` — «не заданы»: ровно то, что отдаёт резолвер, без подмены пустотой. */
  value: Record<string, Array<[string, string]>> | null;
  days: Array<{
    day: string;
    label: string;
    intervals: Array<[string, string]>;
    display: string;
  }>;
  display: string;
  is_set: boolean;
  caption: string;
}

export interface NetworkScreen {
  network: { id: number; name: string; title: string };
  counts: {
    filials: number;
    filials_display: string;
    legal_entities: number;
    legal_entities_display: string;
  };
  words: string[];
  defaults: NetworkDefault[];
  hours: { start: string | null; end: string | null; display: string; readonly: boolean; caption: string };
  reception_hours: { display: string; readonly: boolean; caption: string };
  settings: NetworkSetting[];
  setting_groups: SettingGroup[];
  filials: FilialRow[];
  /** Словарь подсказок «?»: ключ поля → текст. Приходит из базы, не из кода. */
  hints: Record<string, string | null>;
}

export interface FilialSetting {
  key: string;
  label: string;
  value: unknown;
  display: string;
  /** Величина для поля правки — в той единице, какой её называет человек (рубли, а не копейки). */
  edit_value: unknown;
  edit_suffix: string | null;
  origin: 'own' | 'network';
  origin_label: string;
  network_value: unknown;
  network_display: string;
  unit: string;
  can_override: boolean;
  no_override_reason: string | null;
  /**
   * ⛔ `s105` правка 2 — ЧЕСТНЫЙ ЯРЛЫК. `origin_label` отвечает «своё или
   * наследуемое», а `level_label` — «а бывает ли своё вообще»: у сетевого ключа
   * не бывает, и он подписан «одна на сеть» со ссылкой туда, где его правят.
   */
  level: string;
  level_label: string;
  network_href: string | null;
  group: string;
}

export interface FilialBlockRow {
  block: string;
  label: string;
  state: 'enabled' | 'disabled' | 'placed';
  state_label: string;
  enabled_at: string | null;
  enabled_by: string | null;
  disabled_at: string | null;
  disabled_by: string | null;
  disable_reason: string | null;
  fact_count: number | null;
  counter_owner: string | null;
}

export interface LegalEntityOption {
  id: number;
  name: string;
  inn: string;
}

export interface FilialScreen {
  filial: { id: number; name: string; slug: string; status: string };
  counts: {
    legal_entity: string | null;
    cash_registers: number;
    cash_registers_display: string;
    blocks_placed: number;
    blocks_enabled: number;
    blocks_display: string;
  };
  words: string[];
  requisites: {
    address: string | null;
    legal_entity: LegalEntityOption | null;
    legal_entity_options: LegalEntityOption[];
    domains: string[];
  };
  hours: {
    computed_start: string | null;
    computed_end: string | null;
    computed_display: string;
    override_start: string | null;
    override_end: string | null;
    override_display: string;
    override_applied: boolean;
    effective_display: string;
    caption: string;
  };
  settings: FilialSetting[];
  setting_groups: SettingGroup[];
  cash: {
    registers: Array<{
      id: number;
      title: string;
      kind: string;
      kind_label: string;
      is_cash: boolean;
      status: string;
    }>;
    shop: { id: number; name: string; shop_id_yookassa: string; is_active: boolean } | null;
  };
  blocks: FilialBlockRow[];
  procedures: { count: number; display: string; link: string };
  staff: Array<{ id: number; name: string; position: string | null; specialization: string | null }>;
  amo: { pipeline_id: number | null; display: string; origin: 'own' | 'network'; caption: string };
  reception_hours: ReceptionHours;
  hints: Record<string, string | null>;
}

export interface FilialOption {
  id: number | null;
  name: string;
}

export async function fetchNetworkScreen(): Promise<NetworkScreen> {
  const { data } = await apiClient.get<NetworkScreen>('/network');
  return data;
}

export async function fetchFilialScreen(id: number): Promise<FilialScreen> {
  const { data } = await apiClient.get<FilialScreen>(`/network/filials/${id}`);
  return data;
}

export async function fetchFilialOptions(): Promise<FilialOption[]> {
  const { data } = await apiClient.get<{ options: FilialOption[] }>('/network/filial-options');
  return data.options;
}

/**
 * Каждое действие возвращает КАРТОЧКУ ЦЕЛИКОМ, а не «ок».
 *
 * Дорисуй экран новое состояние сам — и владелец видел бы не то, что легло в
 * базу, а то, что фронт про неё думает: переопределение одной настройки двигает
 * и «своё/как в сети», и строку слов в шапке, и след.
 */
export async function overrideSetting(
  filialId: number,
  key: string,
  value: unknown,
): Promise<FilialScreen> {
  const { data } = await apiClient.put<FilialScreen>(
    `/network/filials/${filialId}/settings/${key}`,
    { value },
  );
  return data;
}

export async function setLegalEntity(filialId: number, legalEntityId: number): Promise<FilialScreen> {
  const { data } = await apiClient.put<FilialScreen>(`/network/filials/${filialId}/legal-entity`, {
    legal_entity_id: legalEntityId,
  });
  return data;
}

export async function setHours(
  filialId: number,
  start: string | null,
  end: string | null,
): Promise<FilialScreen> {
  const { data } = await apiClient.put<FilialScreen>(`/network/filials/${filialId}/hours`, {
    start,
    end,
  });
  return data;
}

export async function enableBlock(filialId: number, block: string): Promise<FilialScreen> {
  const { data } = await apiClient.post<FilialScreen>(
    `/network/filials/${filialId}/blocks/${block}/enable`,
    {},
  );
  return data;
}

export async function disableBlock(
  filialId: number,
  block: string,
  reason: string,
): Promise<FilialScreen> {
  const { data } = await apiClient.post<FilialScreen>(
    `/network/filials/${filialId}/blocks/${block}/disable`,
    { reason },
  );
  return data;
}

/**
 * Правка ДЕФОЛТА СЕТИ (`s105`, правка 1) — то, что наследуют все филиалы.
 *
 * Отдельный вызов от `overrideSetting`, и это не дублирование: один задаёт
 * наследуемое, другой — исключение у одной точки, и в следе это разные имена.
 */
export async function setNetworkSetting(key: string, value: unknown): Promise<NetworkScreen> {
  const { data } = await apiClient.put<NetworkScreen>(`/network/settings/${key}`, { value });
  return data;
}

export async function fetchNetworkSettingHistory(key: string): Promise<SettingHistoryEntry[]> {
  const { data } = await apiClient.get<{ key: string; entries: SettingHistoryEntry[] }>(
    `/network/settings/${key}/history`,
  );
  return data.entries;
}

export async function fetchFilialSettingHistory(
  filialId: number,
  key: string,
): Promise<SettingHistoryEntry[]> {
  const { data } = await apiClient.get<{ key: string; entries: SettingHistoryEntry[] }>(
    `/network/filials/${filialId}/settings/${key}/history`,
  );
  return data.entries;
}

/**
 * Часы приёма филиала (`s105`, правка 4) — вывеска для клиентки.
 *
 * `null` — «не заданы»: законное состояние, а не пустая форма. Служебное окно
 * (`setHours`) этот ход не трогает ни одной колонкой.
 */
export async function setReceptionHours(
  filialId: number,
  hours: Record<string, Array<[string, string]>> | null,
): Promise<FilialScreen> {
  const { data } = await apiClient.put<FilialScreen>(
    `/network/filials/${filialId}/reception-hours`,
    { hours },
  );
  return data;
}

/* ═══════════════════ Юрлица (`s105`, правка 5) ═══════════════════ */

export interface LegalEntityRow {
  id: number;
  name: string;
  inn: string;
  filials_count: number;
  filials_display: string;
  can_trash: boolean;
  no_trash_reason: string | null;
}

export interface LegalEntitiesScreen {
  entities: LegalEntityRow[];
  hints: Record<string, string | null>;
}

export async function fetchLegalEntities(): Promise<LegalEntitiesScreen> {
  const { data } = await apiClient.get<LegalEntitiesScreen>('/legal-entities');
  return data;
}

export async function createLegalEntity(name: string, inn: string): Promise<LegalEntitiesScreen> {
  const { data } = await apiClient.post<LegalEntitiesScreen>('/legal-entities', { name, inn });
  return data;
}

export async function updateLegalEntity(
  id: number,
  name: string,
  inn: string,
): Promise<LegalEntitiesScreen> {
  const { data } = await apiClient.put<LegalEntitiesScreen>(`/legal-entities/${id}`, { name, inn });
  return data;
}

export async function trashLegalEntity(id: number, reason: string): Promise<LegalEntitiesScreen> {
  const { data } = await apiClient.delete<LegalEntitiesScreen>(`/legal-entities/${id}`, {
    data: { reason },
  });
  return data;
}
